import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Copy, CopyX, Sparkles, FileUp, ArchiveRestore, ShieldCheck } from 'lucide-react';
import { chipClass, chipStyle } from './categoryUtils';

export const BlockRow = ({ block: b, onOpen, onCopy, catColors, overlapWith, searchMeta, topUsage, highlighted, onRestore, onHistory }) => {
  const usage = (b.copy_count || 0) + (b.include_count || 0);
  return (
    <div className={`group flex items-center gap-2.5 px-3 py-2 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${highlighted ? 'bg-[#4ABFCF]/10 ring-1 ring-inset ring-[#4ABFCF]/40' : ''}`}
      onClick={() => onOpen(b)} data-testid={`rfp-row-${b.id}`}>
      <span className="text-[9px] font-mono font-bold text-[#0F2040] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 shrink-0 w-[4.5rem] text-center"
        data-testid={`rfp-row-capid-${b.id}`}>{b.capability_id || '—'}</span>
      <span className="text-xs font-semibold text-[#0F2040] truncate shrink min-w-0 max-w-[26rem]" title={b.title}>{b.title}</span>
      {b.archived && onRestore && (
        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onRestore(b); }}
          className="h-5 text-[9px] px-1.5 shrink-0 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
          data-testid={`rfp-row-restore-${b.id}`}>
          <ArchiveRestore className="w-2.5 h-2.5 mr-0.5" />Restore
        </Button>
      )}
      {b.review_status === 'pending' && (
        <Badge variant="outline" className="text-[8px] px-1.5 py-0 shrink-0 bg-amber-50 text-amber-700 border-amber-200">Review pending</Badge>
      )}
      {b.scrubbed_at && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onHistory?.(b); }}
          className="inline-flex items-center gap-0.5 text-[8px] px-1.5 py-0 shrink-0 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hidden sm:inline-flex"
          title={`Scrubbed ${new Date(b.scrubbed_at).toLocaleDateString()}${b.scrubbed_by ? ` by ${b.scrubbed_by}` : ''} — open history (previous wording restorable)`}
          data-testid={`rfp-row-scrubbed-${b.id}`}>
          <ShieldCheck className="w-2.5 h-2.5" />Scrubbed {new Date(b.scrubbed_at).toLocaleDateString()}
        </button>
      )}
      {(b.category_names || []).slice(0, 3).map(name => (
        <Badge key={name} variant="outline"
          className={`text-[8px] px-1.5 py-0 shrink-0 hidden sm:inline-flex ${chipStyle(catColors?.[name]) ? '' : chipClass(name)}`}
          style={chipStyle(catColors?.[name]) || undefined}>{name}</Badge>
      ))}
      {overlapWith?.length > 0 && (
        <Badge variant="outline" className="text-[8px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200 shrink-0"
          title={`Wording overlaps heavily with: ${overlapWith.join(', ')}`}
          data-testid={`rfp-row-overlap-${b.id}`}><CopyX className="w-2.5 h-2.5 mr-0.5" />Overlap</Badge>
      )}
      {b.imported_from && (
        <FileUp className="w-3 h-3 text-slate-300 shrink-0 hidden lg:block" title={`Imported from ${b.imported_from}`} />
      )}
      {b.created_at && (Date.now() - new Date(b.created_at).getTime()) < 30 * 86400000 && (
        <span className="text-[8px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-px uppercase tracking-wide shrink-0"
          title={`Added ${b.created_at.slice(0, 10)}`} data-testid={`rfp-row-new-${b.id}`}>New</span>
      )}
      <span className="flex-1 text-[10px] text-slate-400 truncate hidden lg:block min-w-0">
        {(b.keywords || []).slice(0, 4).join(' · ')}
      </span>
      {searchMeta && (
        <span className="text-[9px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-px shrink-0"
          title={searchMeta.matched?.length ? `Matched: ${searchMeta.matched.join(', ')}` : 'Relevance score'}
          data-testid={`rfp-row-score-${b.id}`}>
          {searchMeta.ai_ranked ? <Sparkles className="w-2.5 h-2.5 inline mr-0.5 -mt-px" /> : null}
          {Math.round(searchMeta.score)}
        </span>
      )}
      {topUsage > 0 && usage === topUsage && (
        <span className="text-[8px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-px uppercase tracking-wide shrink-0 hidden sm:block">Most used</span>
      )}
      <span className="text-[9px] text-slate-400 shrink-0 w-14 text-right hidden sm:block"
        title={`Copied ${b.copy_count || 0}× · in ${b.include_count || 0} packs`}>{usage}× used</span>
      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-300 group-hover:text-[#0F2040] shrink-0"
        onClick={(e) => { e.stopPropagation(); onCopy(b); }}
        data-testid={`rfp-row-copy-${b.id}`} title="Copy the block text">
        <Copy className="w-3 h-3" />
      </Button>
    </div>
  );
};
